import { createStore, get, set, del, keys } from "idb-keyval"
import { captureSession, withSubmissionLock } from "@/services/submissionConcurrency"
import type { Photo } from "@capacitor/camera"
import type { ImageConfig } from "@/types/form-builder"
import { environmentStorageKey } from "@/lib/appEnvironment"
import { cameraPhotoToFile } from "@/lib/imageMedia"
import { collectLocalMediaIds, deferMediaUpload, markMediaAttached, type MediaOwner } from "@/services/offlineMediaService"
import { draftKeyFor, mutateDraft } from "@/services/draftService"
import { formDraftDb } from "@/lib/formDraftDb"
import { toast } from "sonner"

const store = createStore(environmentStorageKey("dkro-native-capture"), "captures")
type PendingCapture = { id: string; owner: MediaOwner; config: ImageConfig; timestamp: string }
type QuarantinedPhoto = { photo: Photo; blob?: Blob }
const liveCaptures = new Set<string>()

export async function cancelInterruptedNativeCapture(id: string) {
  const check = captureSession()
  await withSubmissionLock("native-camera", async () => {
    const capture = await get<PendingCapture>("active", store)
    if (!capture || capture.id !== id) return
    captureSession(capture.owner.userId)()
    if (liveCaptures.has(id) || await get(`photo:${id}`, store) || await get(`result:${id}`, store))
      throw new Error("Esta captura já tem um resultado ou ainda está em execução. Tente a recuperação.")
    check()
    await set(`abandoned:${id}`, capture, store)
    await del("active", store)
  })
}

export async function discardNativeSession(owner: MediaOwner, sessionId: string) {
  const matches = (capture?: PendingCapture) => capture?.owner.userId === owner.userId
    && capture.owner.workspaceId === owner.workspaceId && capture.owner.submissionId === owner.submissionId
    && capture.owner.editingSessionId === sessionId
  await withSubmissionLock("native-camera", async () => {
    captureSession(owner.userId)()
    const active = await get<PendingCapture>("active", store)
    if (matches(active)) {
      if (!await get(`photo:${active!.id}`, store) && !await get(`result:${active!.id}`, store))
        await set(`abandoned:${active!.id}`, active, store)
      await finishNativePhoto(active!.id)
    }
    for (const key of await keys(store)) {
      if (!/^(photo|result):/.test(String(key))) continue
      const saved = await get<{ capture: PendingCapture }>(key, store)
      if (matches(saved?.capture)) await finishNativePhoto(saved!.capture.id)
    }
  })
}

export function reportNativeCaptureRecovery(error: unknown) {
  toast.error(String(error), {
    duration: Infinity,
    action: { label: "Tentar recuperação", onClick: () => { void initializeNativeCaptureRecovery().catch(reportNativeCaptureRecovery) } },
  })
}

export async function beginNativeCapture(owner: MediaOwner, config: ImageConfig, id: string = crypto.randomUUID()) {
  return withSubmissionLock("native-camera", async () => {
    const pending = await get<PendingCapture>("active", store)
    if (pending) {
      if (pending.owner.userId === owner.userId && !liveCaptures.has(pending.id)
        && !await get(`photo:${pending.id}`, store) && !await get(`result:${pending.id}`, store)) {
        toast.error("Uma abertura de câmera interrompida não retornou foto. Tente a recuperação ou cancele essa abertura. Qualquer resultado tardio será colocado em quarentena, não atribuído a outra captura.", {
          duration: Infinity,
          action: { label: "Cancelar abertura interrompida", onClick: () => { void cancelInterruptedNativeCapture(pending.id).catch(reportNativeCaptureRecovery) } },
        })
      }
      throw new Error("Uma captura de câmera anterior ainda está com recuperação pendente.")
    }
    captureSession(owner.userId)()
    if (owner.submissionId && owner.editingSessionId) {
      const { assertEditingSession } = await import("@/services/draftService")
      await assertEditingSession(draftKeyFor({ ...owner, submissionId: owner.submissionId }), owner.editingSessionId)
    }
    const capture: PendingCapture = { id, owner, config, timestamp: new Date().toISOString() }
    await set("active", capture, store)
    liveCaptures.add(id)
    return capture.id
  })
}
export async function endNativeCapture(id: string) {
  liveCaptures.delete(id)
  const capture = await get<PendingCapture>("active", store)
  if (capture?.id === id) await del("active", store)
}
export async function persistNativePhoto(id: string, photo: Photo): Promise<File> {
  const result = await get<{ capture: PendingCapture; blob: Blob }>(`result:${id}`, store)
  if (result?.blob.size) return result.blob as File
  const pending = await get<{ capture: PendingCapture; photo: Photo }>(`photo:${id}`, store)
  const capture = pending?.capture ?? await get<PendingCapture>("active", store)
  if (!capture || capture.id !== id) throw new Error("A identidade da captura nativa está indisponível. Mantenha este formulário aberto.")
  await set(`photo:${id}`, { capture, photo }, store)
  const file = await cameraPhotoToFile(photo)
  await set(`result:${id}`, { capture, blob: new Blob([file], { type: file.type }) }, store)
  return file
}
export async function finishNativePhoto(id: string) {
  await del(`photo:${id}`, store)
  await del(`result:${id}`, store)
  await endNativeCapture(id)
}
async function preserveQuarantinedPhoto(key: string, saved: QuarantinedPhoto) {
  if (saved.blob?.size) return
  const file = await cameraPhotoToFile(saved.photo)
  await set(key, { ...saved, blob: new Blob([file], { type: file.type }) }, store)
}

export async function restoreNativePhoto(photo: Photo) {
  await withSubmissionLock("native-camera", async () => {
    const capture = await get<PendingCapture>("active", store)
    const ambiguous = (await keys(store)).some(key => String(key).startsWith("abandoned:"))
    if (ambiguous || !capture?.owner.submissionId || !capture.owner.fieldPath) {
      const key = `unassigned:${crypto.randomUUID()}`
      await set(key, { photo }, store)
      await preserveQuarantinedPhoto(key, { photo })
      throw new Error("A saída de câmera recuperada não tem um dono verificado inequívoco. Os bytes estão em quarentena neste aparelho.")
    }
    await persistNativePhoto(capture.id, photo)
    await attachRestoredCapture(capture.id)
  })
}
async function attachRestoredCapture(id: string) {
  const saved = await get<{ capture: PendingCapture; blob: Blob }>(`result:${id}`, store)
  if (!saved) return
  const { capture, blob } = saved
  const owner = capture.owner as MediaOwner & { submissionId: string; fieldPath: string }
  const key = draftKeyFor(owner)
  await mutateDraft(key, async () => {
    if (owner.editingSessionId) {
      const session = await formDraftDb.sessions.get(key)
      const { hasAcceptedSession } = await import("@/stores/useSubmissionQueueStore")
      if (session?.sessionId !== owner.editingSessionId || session.phase !== "editing" || await hasAcceptedSession(key, owner.editingSessionId)) {
        await finishNativePhoto(id)
        return
      }
    }
    const draft = await formDraftDb.drafts.get(key)
    if (!draft || draft.userId !== owner.userId || draft.workspaceId !== owner.workspaceId) return
    if (draft.status === "completed" || draft.completionRequestedAt) throw new Error("A captura recuperada pertence a uma submissão concluída. Os bytes foram mantidos.")
    const value = await deferMediaUpload(blob, { timestamp: capture.timestamp }, owner, { image: capture.config }, true, capture.id)
    if (!collectLocalMediaIds(draft.data).has(value.localMediaId)) {
      const data = structuredClone(draft.data)
      const parts = owner.fieldPath.split(".")
      let target: Record<string, unknown> = data
      for (let i = 0; i < parts.length - 1; i++) target = (target[parts[i]] ??= /^\d+$/.test(parts[i + 1]) ? [] : {}) as Record<string, unknown>
      const field = parts[parts.length - 1]
      const previous = Array.isArray(target[field]) ? target[field] as unknown[] : []
      target[field] = [...previous, value]
      await formDraftDb.drafts.put({ ...draft, data, revision: crypto.randomUUID(), status: "editing", updatedAt: Date.now() })
    }
    await markMediaAttached(value)
    await finishNativePhoto(id)
  })
}

export async function hasPendingNativeCapture(owner: MediaOwner) {
  const active = await get<PendingCapture>("active", store)
  if (active?.owner.userId === owner.userId && active.owner.workspaceId === owner.workspaceId && active.owner.submissionId === owner.submissionId) return true
  for (const key of await keys(store)) {
    if (!/^(result|photo):/.test(String(key))) continue
    const result = await get<{ capture: PendingCapture }>(key, store)
    const candidate = result?.capture.owner
    if (candidate?.userId === owner.userId && candidate.workspaceId === owner.workspaceId && candidate.submissionId === owner.submissionId) return true
  }
  return false
}

let listenerReady: Promise<unknown> | undefined
let recovering: Promise<void> | undefined
export async function initializeNativeCaptureRecovery() {
  if (typeof window === "undefined") return
  const { Capacitor } = await import("@capacitor/core")
  if (!Capacitor.isNativePlatform()) return
  const { App } = await import("@capacitor/app")
  const report = reportNativeCaptureRecovery
  listenerReady ??= App.addListener("appRestoredResult", result => {
    if (result.pluginId !== "Camera" || result.methodName !== "getPhoto") return
    if (result.success) void restoreNativePhoto(result.data as Photo).catch(report)
    else void withSubmissionLock("native-camera", async () => {
      if ((await keys(store)).some(key => String(key).startsWith("abandoned:"))) return
      const capture = await get<PendingCapture>("active", store)
      if (capture) await endNativeCapture(capture.id)
    }).catch(report)
  }).catch(error => { listenerReady = undefined; throw error })
  await listenerReady
  recovering ??= (async () => {
    const errors: unknown[] = []
    for (const key of await keys(store)) {
      if (!String(key).startsWith("unassigned:")) continue
      try {
        const saved = await get<QuarantinedPhoto>(key, store)
        if (saved) await preserveQuarantinedPhoto(String(key), saved)
      } catch (error) { errors.push(error) }
    }
    for (const key of await keys(store)) {
      if (!String(key).startsWith("photo:")) continue
      try {
        const saved = await get<{ capture: PendingCapture; photo: Photo }>(key, store)
        if (saved) await persistNativePhoto(saved.capture.id, saved.photo)
      } catch (error) { errors.push(error) }
    }
    for (const key of await keys(store)) {
      if (!String(key).startsWith("result:")) continue
      try { await attachRestoredCapture(String(key).slice(7)) }
      catch (error) { errors.push(error) }
    }
    if (errors.length) throw new AggregateError(errors, "Algumas fotos capturadas ainda estão com recuperação pendente. Tente novamente antes de enviar.")
  })().finally(() => { recovering = undefined })
  return recovering
}
